import React, { ReactHTML, useLayoutEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useForceUpdate } from '../hooks/forceUpdate';

const PLACEHOLDER_ATTR = 'data-head-placeholder';
const PLACEHOLDER_HASH_ATTR = 'data-head-placeholder-hash';
const ELEMENT_ATTR = 'data-head-element';

function createPlaceholder<T extends HTMLElement>(
  tagName: keyof ReactHTML,
  hash: string,
  key: string,
): T {
  const element = document.createElement(tagName) as T;
  element.setAttribute(PLACEHOLDER_ATTR, `${key}`);
  element.setAttribute(PLACEHOLDER_HASH_ATTR, `${hash}`);
  return element;
}

/**
 * Convert an array or record of key-value pairings into a hash to uniquely
 * identify DOM elements owned by this component.
 */
function hashKeyAttrs(attrs: readonly [string, string | null][] | Record<string, string | null>) {
  if (Array.isArray(attrs)) {
    // Convert the key~value pairings to base64
    const z = attrs
      .filter(([, value]) => value !== null)
      .map(([key, value]) => `${key}~${value}`)
      .join('^');

    return btoa(z);
  }

  return btoa(
    Object.entries(attrs)
      .map(([key, value]) => `${key}~${value}`)
      .join(','),
  );
}

function isElementOverlapping(
  element: Element,
  keyAttrs: readonly [string, string | null][] | Record<string, string | null>,
) {
  if (Array.isArray(keyAttrs)) {
    return keyAttrs.some(([key, value]) => value && element.getAttribute(key) === value);
  }

  return Object.entries(keyAttrs).every(([key, value]) => element.getAttribute(key) === value);
}

/**
 *
 * @param tagName the DOM element tag (e.g. `a`)
 * @param container the element container
 * @param keyAttrs an object mapping "key" attributes which should be used to uniquely identify the DOM element within `container`
 * @returns a reference to the created DOM element
 */
const useDOMElement = <T extends HTMLElement>(
  tagName: keyof ReactHTML,
  container: HTMLElement,
  keyAttrs: readonly [string, string | null][] | Record<string, string | null> = {},
): { ref: React.MutableRefObject<T | null> } => {
  // Memoize the hash for the 'key' attributes
  // 'key' attributes uniquely identify a DOM element within its container,
  // for example, `<meta>` tags may be uniquely identified by a combination of
  // their `name=` and `property=` values.
  const hash = useMemo(() => hashKeyAttrs(keyAttrs), [keyAttrs]);
  // Hash specifically avoid re-rendering if the relevant values haven't changed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoized = useMemo(() => ({ keyAttrs, hash }), [hash]);

  // React does not re-render when a ref is updated,
  // so we trigger a re-render using forceUpdate.
  const forceUpdate = useForceUpdate();

  // The reference to the current DOM element
  const elementRef = useRef<T | null>(null);

  useLayoutEffect(() => {
    const replacedElements = new Map<string, Element>();

    // Query all DOM elements in this container with the same tag...
    const existing = container.querySelectorAll(`${tagName}`);

    // Remove any elements which overlap with the element we're rendering
    // (e.g.) `<meta property="og:title" content="Hello :)" />` overlaps
    // with `<meta property="og:title" content="Hi!" />`
    const lastIndex = Array.from(existing).reduce((index, element) => {
      if (index < 0) return index;

      if (
        isElementOverlapping(element, memoized.keyAttrs) &&
        // Don't handle the element if it matches our current ref
        element !== elementRef.current &&
        // Don't re-replace a placeholder
        !element.hasAttribute(PLACEHOLDER_ATTR)
      ) {
        // Don't modify other elements controlled by this hook
        if (element.hasAttribute(ELEMENT_ATTR)) {
          return -1;
        }

        // The 'key' for the element
        // is the hash it was found in
        // plus its index.
        const key = `${index++}`;

        // Replace the overriden element with a placeholder
        const replacedChild = container.replaceChild(
          createPlaceholder(tagName, memoized.hash, key),
          element,
        );

        // Store the removed element
        replacedElements.set(key, replacedChild);
      }

      return index;
    }, 0);

    // A conflicting element from this hook already exists...
    if (lastIndex < 0) {
      return;
    }

    // Create the element
    const element = document.createElement(tagName) as T;
    element.setAttribute(ELEMENT_ATTR, 'true');
    // Prepend because some services only fetch the first tags/bytes of the HTML page
    container.prepend(element);
    elementRef.current = element;
    // React won't re-render based on assigning a reference, so force a re-render
    forceUpdate();

    return () => {
      elementRef.current = null;
      container.removeChild(element);

      // Find all placeholders
      const placeholderElements = Array.from(
        container.querySelectorAll(`${tagName}[${PLACEHOLDER_HASH_ATTR}='${memoized.hash}']`),
      );

      for (const placeholderElement of placeholderElements) {
        const key = placeholderElement.getAttribute(PLACEHOLDER_ATTR);

        // If the placeholder is missing a key, remove it.
        if (!key) {
          container.removeChild(placeholderElement);
          continue;
        }

        // If this placeholder exists in this hook's replaced elements,
        // restore the original element.
        const originalElement = replacedElements.get(key);
        if (originalElement) {
          container.replaceChild(originalElement, placeholderElement);
        }
      }
    };
  }, [memoized, container, forceUpdate, tagName]);

  // Return the reference...
  return { ref: elementRef };
};

const metaAttrs = ['name', 'property', 'content'] as const;

const HeadMeta: React.FC<{
  name?: string;
  property?: string;
  content?: string;
}> = props => {
  const { name, property } = props;
  const { ref } = useDOMElement<HTMLMetaElement>('meta', document.head, [
    // HeadMeta should override any <meta /> tag with a matching name or property
    ['name', name ?? null],
    ['property', property ?? null],
  ]);

  if (!ref.current) {
    return null;
  }

  for (const attr of metaAttrs) {
    const value = props[attr];

    if (value) {
      ref.current.setAttribute(attr, `${value}`);
    } else {
      ref.current.removeAttribute(attr);
    }
  }

  return createPortal(null, ref.current);
};

const HeadTitle: React.FC<{
  children: string | string[];
}> = ({ children }) => {
  const { ref } = useDOMElement<HTMLTitleElement>('title', document.head);

  if (!ref.current) {
    return null;
  }

  // Render the provided React children (e.g. a string or array of strings)
  // in the DOM element from `useDOMElement`.
  return createPortal(children, ref.current);
};

interface HeadChildren {
  Meta: typeof HeadMeta;
  Title: typeof HeadTitle;
}

const HeadComponent: React.FC = ({ children }) => {
  // Reverse the children because these elements are prepended, not appended.
  const mappedChildren = React.Children.toArray(children);
  mappedChildren.reverse();

  return (
    <>
      {mappedChildren.map(child => {
        if (!React.isValidElement(child)) {
          return null;
        }

        if (child.type === 'meta' || child.type === HeadMeta) {
          return <HeadMeta key={child.key} {...child.props} />;
        }

        if (child.type === 'title' || child.type === HeadTitle) {
          return <HeadTitle key={child.key} {...child.props} />;
        }

        return null;
      })}
    </>
  );
};

export const Head: HeadChildren & React.FC = Object.assign(HeadComponent, {
  Title: HeadTitle,
  Meta: HeadMeta,
});
